import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

interface ClockEvent {
  id: string;
  event_type: string;
  event_time: string;
  created_at: string;
  original_event_id: string | null;
  source: string;
  correction_type: string | null;
  correction_reason: string | null;
}

interface DailyTimesheet {
  staff_id: string;
  full_name: string;
  work_date: string;
  total_work_seconds: number;
  total_break_seconds: number;
  shift_count: number;
  status: string;
}

interface MonthlyTimesheet {
  staff_id: string;
  full_name: string;
  year_month: string;
  total_work_seconds: number;
  total_break_seconds: number;
  shift_count: number;
}

type ClockStatus = 'off' | 'working' | 'break';

const EVENT_LABELS: Record<string, string> = {
  clock_in: '出勤',
  break_start: '休憩開始',
  break_end: '休憩終了',
  clock_out: '退勤',
};

export default function AttendancePage() {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [status, setStatus] = useState<ClockStatus>('off');
  const [clocking, setClocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dailyTimesheet, setDailyTimesheet] = useState<DailyTimesheet | null>(null);
  const [monthlyTimesheet, setMonthlyTimesheet] = useState<MonthlyTimesheet | null>(null);
  const [history, setHistory] = useState<ClockEvent[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Live clock
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const fetchStatusAndHistory = useCallback(async () => {
    if (!user) return;
    setLoadingData(true);
    try {
      const { data: events, error: histError } = await supabase
        .from('time_clock_events')
        .select('id, event_type, event_time, created_at, original_event_id, source, correction_type, correction_reason')
        .eq('staff_id', user.id)
        .order('event_time', { ascending: false })
        .limit(10);

      if (histError) throw histError;

      const eventList = (events || []) as ClockEvent[];
      setHistory(eventList);

      // Determine current status from latest event
      if (eventList.length > 0) {
        const latest = eventList[0];
        if (latest.event_type === 'clock_in' || latest.event_type === 'break_end') {
          setStatus('working');
        } else if (latest.event_type === 'break_start') {
          setStatus('break');
        } else {
          setStatus('off');
        }
      } else {
        setStatus('off');
      }

      // Fetch daily timesheet
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const { data: dailyData, error: dailyError } = await supabase
        .rpc('compute_daily_timesheet', {
          p_staff_id: user.id,
          p_from_date: todayStr,
          p_to_date: todayStr,
        });

      if (!dailyError && dailyData && dailyData.length > 0) {
        setDailyTimesheet(dailyData[0] as DailyTimesheet);
      } else {
        setDailyTimesheet({ staff_id: '', full_name: '', work_date: '', total_work_seconds: 0, total_break_seconds: 0, shift_count: 0, status: 'no_data' });
      }

      // Fetch monthly timesheet
      const { data: monthlyData, error: monthlyError } = await supabase
        .rpc('compute_monthly_timesheet', {
          p_staff_id: user.id,
          p_year: today.getFullYear(),
          p_month: today.getMonth() + 1,
        });

      if (!monthlyError && monthlyData && monthlyData.length > 0) {
        setMonthlyTimesheet(monthlyData[0] as MonthlyTimesheet);
      } else {
        setMonthlyTimesheet({ staff_id: '', full_name: '', year_month: '', total_work_seconds: 0, total_break_seconds: 0, shift_count: 0 });
      }
    } catch {
      setError('データの取得に失敗しました');
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStatusAndHistory();
  }, [fetchStatusAndHistory]);

  const handleClock = async (eventType: string) => {
    if (clocking) return;
    setClocking(true);
    setError(null);

    try {
      const { error: rpcError } = await supabase
        .rpc('record_time_clock_event', { p_event_type: eventType });

      if (rpcError) {
        setError('打刻に失敗しました');
        setClocking(false);
        return;
      }

      await fetchStatusAndHistory();
    } catch {
      setError('打刻に失敗しました');
    } finally {
      setClocking(false);
    }
  };

  const formatMinutes = (seconds: number) => {
    const totalMinutes = Math.floor(seconds / 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}時間${m}分`;
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
  };

  const jstDate = currentTime.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
  const jstTime = currentTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="max-w-3xl">
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground-900 font-heading">勤怠管理</h2>
        <p className="text-sm text-foreground-600 mt-1">{jstDate} {jstTime}</p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-error-warning-line text-sm"></i>
          </span>
          {error}
        </div>
      )}

      {/* Status & Clock Card */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden mb-6">
        <div className="p-6">
          {/* Status Badge */}
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-3 h-3 rounded-full ${
              status === 'working' ? 'bg-accent-500 animate-pulse' :
              status === 'break' ? 'bg-secondary-500' :
              'bg-background-400'
            }`}></div>
            <span className="text-base font-semibold text-foreground-900">
              {status === 'working' ? '勤務中' : status === 'break' ? '休憩中' : '勤務外'}
            </span>
          </div>

          {/* Clock Buttons */}
          <div className="flex items-center gap-3">
            {status === 'off' && (
              <button
                onClick={() => handleClock('clock_in')}
                disabled={clocking}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-background-50 text-base font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${clocking ? 'ri-loader-4-line animate-spin' : 'ri-login-box-line'} text-lg`}></i>
                </span>
                出勤
              </button>
            )}
            {status === 'working' && (
              <>
                <button
                  onClick={() => handleClock('break_start')}
                  disabled={clocking}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-secondary-500 hover:bg-secondary-600 text-background-50 text-base font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${clocking ? 'ri-loader-4-line animate-spin' : 'ri-cup-line'} text-lg`}></i>
                  </span>
                  休憩開始
                </button>
                <button
                  onClick={() => handleClock('clock_out')}
                  disabled={clocking}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 text-base font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${clocking ? 'ri-loader-4-line animate-spin' : 'ri-logout-box-line'} text-lg`}></i>
                  </span>
                  退勤
                </button>
              </>
            )}
            {status === 'break' && (
              <button
                onClick={() => handleClock('break_end')}
                disabled={clocking}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-background-50 text-base font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${clocking ? 'ri-loader-4-line animate-spin' : 'ri-play-circle-line'} text-lg`}></i>
                </span>
                休憩終了
              </button>
            )}
          </div>
        </div>

        {/* Time Summary */}
        <div className="grid grid-cols-3 border-t border-background-200/70">
          <div className="px-5 py-4 text-center border-r border-background-200/70">
            <p className="text-xs text-foreground-600 mb-1">本日勤務時間</p>
            <p className="text-lg font-bold text-foreground-900 font-heading">
              {loadingData ? '--' : formatMinutes(dailyTimesheet?.total_work_seconds || 0)}
            </p>
          </div>
          <div className="px-5 py-4 text-center border-r border-background-200/70">
            <p className="text-xs text-foreground-600 mb-1">本日休憩時間</p>
            <p className="text-lg font-bold text-foreground-900 font-heading">
              {loadingData ? '--' : formatMinutes(dailyTimesheet?.total_break_seconds || 0)}
            </p>
          </div>
          <div className="px-5 py-4 text-center">
            <p className="text-xs text-foreground-600 mb-1">今月勤務時間</p>
            <p className="text-lg font-bold text-foreground-900 font-heading">
              {loadingData ? '--' : formatMinutes(monthlyTimesheet?.total_work_seconds || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Recent History */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-900">最近の打刻履歴</h3>
        </div>
        <div className="overflow-x-auto">
          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-primary-500"></i>
              </span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-sm text-foreground-500">
              打刻履歴はまだありません
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">種類</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">日時</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">時刻</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">備考</th>
                </tr>
              </thead>
              <tbody>
                {history.map((event) => (
                  <tr key={event.id} className="border-b border-background-200/70 hover:bg-background-100 transition-colors">
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        event.event_type === 'clock_in' ? 'bg-accent-100 text-accent-800' :
                        event.event_type === 'clock_out' ? 'bg-primary-100 text-primary-800' :
                        'bg-secondary-100 text-secondary-800'
                      }`}>
                        {EVENT_LABELS[event.event_type] || event.event_type}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-foreground-700">{formatDate(event.event_time)}</td>
                    <td className="px-5 py-3 text-foreground-900 font-medium">{formatTime(event.event_time)}</td>
                    <td className="px-5 py-3 text-xs text-foreground-500">
                      {event.source === 'admin_correction' ? (
                        <span className="text-secondary-700">管理者訂正</span>
                      ) : (
                        '--'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
